<template>
  <div class="max-w-[720px] w-full px-4 sm:px-0 sm:mr-5">
    <h2 class="w-full text-center mb-8">
      {{ $t('messages.menu.sign_in_history') }}
    </h2>
    <div class="list--account flex flex-col border border-gray-200 bg-white rounded-lg p-4 min-h-[240px] sm:ml-10">
      <div
        v-for="(records, date) in groupedLoginHistory"
        :key="date"
        class="item--day"
      >
        <h3 class="p-4">
          {{ date }}
        </h3>
        <div
          v-for="record in records"
          :key="record.created_at"
          class="flex block ml-8 mb-8 border-b border-zyxel-gray-300"
        >
          <div class="hidden sm:block h-12 w-12 mr-8 mb-4">
            <img
              class="w-12 h-12"
              :src="getDeviceIcon(record.device)"
              alt="device icon"
            >
          </div>
          <div class="caption w-full mb-4">
            <div>
              <strong>{{ record.os }} • </strong>{{ record.browser }}
            </div>
            <div class="b2 flex md:items-center">
              <img
                v-if="record.provider === 'google'"
                class="w-5 h-5"
                src="@/assets/svg/icon_48px_Google.svg"
                alt="Google sign-in"
              >
              <img
                v-else-if="record.provider === 'apple'"
                class="w-5 h-5"
                src="@/assets/svg/icon_48px_Apple_2.svg"
                alt="Apple sign-in"
              >
              <img
                v-else-if="record.provider === 'entra'"
                class="w-5 h-5"
                src="@/assets/svg/icon_48px_Microsoft.svg"
                alt="Entra sign-in"
              >
              <img
                v-else-if="record.provider === 'passkey'"
                class="w-5 h-5"
                src="@/assets/svg/icon_32px_passkey.svg"
                alt="Passkey sign-in"
              >
              <img
                v-else
                class="w-5 h-5"
                src="@/assets/svg/icon_48px_email_2.svg"
                alt="password sign-in"
              >
              {{ record.ip_address }} • {{ record.location || 'Unknown Location' }} • {{
                formatTime(record.created_at) }}
            </div>
            <div
              v-if="record.is_active_session"
              class="b2 text-zyxel-gray-800 flex items-center"
            >
              <img
                class="w-5 h-5"
                src="@/assets/svg/icon_48px_tickCircle_Green_2.svg"
                alt="Current session"
              >{{
                $t('messages.sign_in_history.current_session') }}
            </div>
          </div>
          <div class="logout">
            <button
              v-if="record.has_active_tokens"
              class="w-8 h-8 cursor-pointer"
              aria-label="Sign out this session"
              @click="openSignOutDialog(record)"
            >
              <img
                class="w-8 h-8"
                src="@/assets/svg/icon_48px_signout.svg"
                alt="logout"
              >
            </button>
          </div>
        </div>
      </div>
      <button
        v-if="hasMore"
        class="b1 text-center block text-zyxel-green-700 font-bold"
        @click="loadMoreHistory"
      >
        {{ $t('messages.sign_in_history.see_more')
        }}
      </button>
    </div>
    <SignOutDialog
      :is-visible="showSignOutDialog"
      :record="selectedRecord"
      @confirm="handleSignOutConfirm"
      @cancel="handleSignOutCancel"
    />
  </div>
</template>
<script>
import { useAuthStore } from '@/stores/auth';
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import SignOutDialog from '@/components/SignOutDialog.vue'
import { logger } from '@/utils/logger';

export default {
  name: 'LoginHistory',
  components: {
      SignOutDialog
  },
  data() {
      return {
          loginHistory: [],
          currentPage: 1,
          isLoading: false,
          hasMore: false,
          showSignOutDialog: false,
          selectedRecord: null
      };
  },
  computed: {
    isDemoAccount() {
      const authStore = useAuthStore();
      return authStore.isDemoAccount;
    },
    groupedLoginHistory() {
      return this.loginHistory.reduce((acc, record) => {
        const recordDate = new Date(record.created_at + 'Z');
        const formattedDate = this.formatDate(recordDate);

        if (!acc[formattedDate]) {
          acc[formattedDate] = [];
        }
        acc[formattedDate].push(record);
        return acc;
      }, {});
    }
  },
  async created() {
    await this.loadHistory();
  },
  methods: {
    async loadHistory() {
      this.isLoading = true;
      try {
        const response = await axios.get(`${apiBaseUrl}/api/users/me/login_history`, {
          params: { page: this.currentPage }
        });
        const newHistory = response.data.data;
        if (this.isDemoAccount) {
          this.loginHistory = newHistory.length > 0 ? [newHistory[0]] : [];
          this.hasMore = false;
        } else {
          this.loginHistory = [...this.loginHistory, ...newHistory];
          this.hasMore = response.data.pageSize * response.data.page < response.data.total;
        }
        this.currentPage += 1;
      } catch (error) {
        logger.error('API error:', error);
      } finally {
        this.isLoading = false;
      }
    },
    loadMoreHistory(event) {
      event.preventDefault();
      if (!this.isLoading) {
        this.loadHistory();
      }
    },
    formatDate(date) {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const isToday = date.toDateString() === today.toDateString();
      const isYesterday = date.toDateString() === yesterday.toDateString();

      if (isToday) {
        return this.$t('messages.sign_in_history.today');
      } else if (isYesterday) {
        return this.$t('messages.sign_in_history.yesterday');
      } else {
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
      }
    },
    getDeviceIcon(device) {
      if (device.includes("mobile")) {
        return require('@/assets/svg/icon_48px_mobile.svg');
      }
      if (device.includes('tablet')) {
        return require('@/assets/svg/icon_48px_tablet.svg');
      }
      return require('@/assets/svg/icon_48px_pc.svg');
    },
    formatTime(dateTime) {
      const date = new Date(dateTime + 'Z');

      const offset = -date.getTimezoneOffset();
      const offsetHours = Math.floor(Math.abs(offset) / 60);
      const offsetMinutes = Math.abs(offset) % 60;
      const timezoneFormatted = `UTC${offset >= 0 ? '+' : '-'}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

      return `${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })} (${timezoneFormatted})`;
    },
    async openSignOutDialog(record) {
      this.selectedRecord = record;
      this.showSignOutDialog = true;
    },
    async updateLoginHistory() {
      this.loginHistory = [];
      this.currentPage = 1;
      await this.loadHistory();
    },

    async handleSignOutConfirm(record) {
      const authStore = useAuthStore();
      logger.log(record);
      if (record.is_active_session) {
        authStore.logout();
      } else {
        await authStore.signoutLogin(record.login_id);
        await this.updateLoginHistory();
        this.showSignOutDialog = false;
      }
    },
    handleSignOutCancel() {
      this.showSignOutDialog = false;
    }
  }
}
</script>
